import { css } from '@emotion/react';
import { Clear } from '@mui/icons-material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Button, IconButton, Stack, Typography } from '@mui/material';
import { useSetAtom } from 'jotai';
import { ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import { ControllerRenderProps, useFormContext } from 'react-hook-form';
import { IBaseFieldProps } from '../../../components/Form/Display/types';
import { FieldFactory } from '../../../components/Form/FieldFactory';
import { RendererType } from '../../../data-models/field-definition';
import { IField, ISimpleChoice } from '../../../data-models/field2.data-model';
import { KpiCollectionConfiguration } from '../../../schemas/KpiCollectionConfiguration.schema';
import { colors } from '../../../theme/colors';
import { createFormField } from '../../../view-models/form.view-model';
import { NoDataMessage2 } from '../../CompanyProfiles/components/Messages/HelpMessage';
import { selectedKpiConfigIdAtom, showKpiConfigFormAtom } from '../kpi-config-form/kpi-config-form-state';

const tableStyles = css`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
  border: 1px solid ${colors.neutral[20]};
  border-radius: 4px;
  background: ${colors.primary[0]};
`;

const rowStyles = css`
  @keyframes rowSlideIn {
    from {
      opacity: 0;
      transform: translateY(-8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  animation: rowSlideIn 0.15s ease-out;

  &:not(:last-child) {
    border-bottom: 1px solid ${colors.neutral[20]};
  }

  &:hover {
    background-color: ${colors.neutral[5]};
  }
`;

const nameCellStyles = css`
  padding: 0.625rem 1rem;
  color: ${colors.primary[100]};
`;

const actionsCellStyles = css`
  padding: 0.25rem 0.5rem;
  text-align: right;
  white-space: nowrap;
  width: 80px;
`;

const addKpiRowStyles = css`
  & td {
    padding: 0.5rem;
  }
`;

type KpiConfigWithId = IField<unknown> & {
  id: number;
};

type KpiConfigListHeader = {
  title: string;
  icon?: ReactNode;
};

type KpiConfigListProps = {
  kpiConfigs: IField<unknown>[];
  header: KpiConfigListHeader;
};

export function KpiConfigList({ kpiConfigs, header }: KpiConfigListProps) {
  const { watch, setValue } = useFormContext<KpiCollectionConfiguration>();
  const allKpis = watch('kpiIds');

  const setShowKpiConfigForm = useSetAtom(showKpiConfigFormAtom);
  const setSelectedKpiConfigId = useSetAtom(selectedKpiConfigIdAtom);

  const kpiConfigsById = useMemo(() => {
    return kpiConfigs.reduce((acc, kpi) => {
      acc.set(kpi.id, kpi);
      return acc;
    }, new Map<number, IField<unknown>>());
  }, [kpiConfigs]);

  // Filter to only KPIs that belong to this list (quantitative or qualitative)
  const selectedIds = useMemo(
    () => allKpis?.filter((id) => kpiConfigsById.has(id)) ?? [],
    [allKpis, kpiConfigsById]
  );

  const items = useMemo<KpiConfigWithId[]>(() => {
    return selectedIds
      .map((id) => kpiConfigsById.get(id))
      .filter((kpi): kpi is IField<unknown> => kpi !== undefined)
      .map((kpi) => ({ ...kpi, id: kpi.id }));
  }, [selectedIds, kpiConfigsById]);

  const selectOptions = useMemo<ISimpleChoice<number>[]>(() => {
    const selectedSet = new Set(selectedIds);
    return (
      kpiConfigs
        .filter((kpi) => !selectedSet.has(kpi.id))
        .map((kpi) => ({
          displayName: kpi.displayName,
          value: kpi.id,
        })) ?? []
    );
  }, [kpiConfigs, selectedIds]);

  const handleEdit = useCallback(
    (_kpi: KpiConfigWithId) => {
      setSelectedKpiConfigId(_kpi.id);
      setShowKpiConfigForm(true);
    },
    [setSelectedKpiConfigId, setShowKpiConfigForm]
  );

  const tableBottomRef = useRef<HTMLDivElement>(null);
  const shouldScrollRef = useRef(false);

  const handleAdd = useCallback(
    (id: number) => {
      setValue('kpiIds', [...(allKpis ?? []), id], { shouldDirty: true });
      shouldScrollRef.current = true;
    },
    [allKpis, setValue]
  );

  useEffect(() => {
    if (shouldScrollRef.current) {
      scrollIntoViewIfHidden(tableBottomRef.current, { behavior: 'smooth', block: 'end' });
      shouldScrollRef.current = false;
    }
  }, [items]);

  const handleDelete = useCallback(
    (kpi: KpiConfigWithId) => {
      setValue('kpiIds', allKpis?.filter((id) => id !== kpi.id) ?? [], { shouldDirty: true });
    },
    [allKpis, setValue]
  );

  const handleClearAll = useCallback(() => {
    const selectedSet = new Set(selectedIds);
    setValue('kpiIds', allKpis?.filter((id) => !selectedSet.has(id)) ?? [], { shouldDirty: true });
  }, [allKpis, selectedIds, setValue]);

  const selectorProps = useMemo(() => {
    const formField = createFormField({
      key: 'addKpi',
      renderer: RendererType.singleSelect,
      rendererMeta: {
        values: selectOptions,
      },
      placeholder: 'Add',
    });

    return {
      formProps: { value: null, onChange: handleAdd } as ControllerRenderProps,
      formField,
    } as IBaseFieldProps<unknown>;
  }, [selectOptions, handleAdd]);

  if (items.length === 0 && selectOptions.length === 0) {
    return <NoDataMessage2 message='No KPIs available' sx={{ height: 'unset' }} />;
  }

  return (
    <>
      <Stack display='grid' gridTemplateColumns='auto 1fr auto' alignItems='center' gap='0.5rem' mt='1rem'>
        {header.icon}
        <Typography variant='h6'>{header.title}</Typography>
        {selectedIds.length > 0 && (
          <Button size='small' onClick={handleClearAll}>
            Clear All
          </Button>
        )}
      </Stack>
      <table css={tableStyles}>
        <tbody>
          {items.map((item) => (
            <KpiConfigListItem key={item.id} config={item} onEdit={handleEdit} onDelete={handleDelete} />
          ))}
          {selectOptions.length > 0 && (
            <tr css={addKpiRowStyles}>
              <td colSpan={2}>
                <FieldFactory {...selectorProps} key={selectOptions.length} />
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <div ref={tableBottomRef} />
    </>
  );
}

interface KpiConfigListItemProps {
  config: KpiConfigWithId;
  onEdit: (config: KpiConfigWithId) => void;
  onDelete: (config: KpiConfigWithId) => void;
}

function KpiConfigListItem({ config, onEdit, onDelete }: KpiConfigListItemProps) {
  return (
    <tr css={rowStyles}>
      <td css={nameCellStyles}>{config.displayName}</td>
      <td css={actionsCellStyles}>
        <IconButton size='small' onClick={() => onEdit(config)}>
          <EditOutlinedIcon fontSize='small' />
        </IconButton>
        <IconButton size='small' onClick={() => onDelete(config)}>
          <Clear fontSize='small' />
        </IconButton>
      </td>
    </tr>
  );
}

function scrollIntoViewIfHidden(element: HTMLElement | null, options?: ScrollIntoViewOptions): void {
  if (!element) {
    return;
  }

  const rect = element.getBoundingClientRect();
  const isVisible = rect.top >= 0 && rect.top <= window.innerHeight;

  if (!isVisible) {
    element.scrollIntoView(options);
  }
}

/** The list's table styling, shared with the frosted preview of this screen. */
export const kpiConfigListCss = {
  table: tableStyles,
  row: rowStyles,
  nameCell: nameCellStyles,
  actionsCell: actionsCellStyles,
  addRow: addKpiRowStyles,
};
